import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Server, HeadphonesIcon, Download, Check, MessageCircle, ChevronRight, Shield, Clock } from 'lucide-react';
import { mockData } from '../mock';

const Home = () => {
  const [activeFeature, setActiveFeature] = useState(0);

  const handleWhatsAppClick = () => {
    window.open(mockData.whatsappLink, '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              nja
            </div>
          </div>
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#features" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Fitur</a>
            <a href="#testimonials" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">Testimoni</a>
            <a href="#faq" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">FAQ</a>
          </nav>
          <Button 
            onClick={handleWhatsAppClick}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg shadow-blue-200 transition-all duration-300 hover:shadow-xl"
          >
            <MessageCircle className="mr-2 h-4 w-4" />
            Hubungi Kami
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-yellow-50/30 -z-10"></div>
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-6">
            <div className="inline-block">
              <span className="px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold border border-blue-200">
                Hosting RDM Terpercaya
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
              Solusi Hosting untuk
              <span className="block bg-gradient-to-r from-blue-600 via-blue-700 to-yellow-600 bg-clip-text text-transparent">
                Rapor Digital Madrasah
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Layanan hosting profesional khusus sistem RDM dengan server handal, support responsif, dan instalasi gratis. Fokus pada madrasah Anda, biarkan kami yang mengurus infrastruktur teknologi.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6">
              <Button 
                onClick={handleWhatsAppClick}
                size="lg" 
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-lg px-8 py-6 shadow-xl shadow-blue-200 hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
              >
                <MessageCircle className="mr-2 h-5 w-5" />
                Konsultasi Gratis
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 text-lg px-8 py-6 transition-all duration-300"
              >
                Lihat Fitur Unggulan
              </Button>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto pt-16">
              {mockData.stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-yellow-600 bg-clip-text text-transparent">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-600 mt-2">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Fitur Unggulan Hosting RDM
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Infrastruktur hosting yang dirancang khusus untuk mendukung operasional Rapor Digital Madrasah Anda
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mockData.features.map((feature, index) => {
              const Icon = feature.icon === 'Server' ? Server : 
                          feature.icon === 'HeadphonesIcon' ? HeadphonesIcon : 
                          Download;
              return (
                <Card 
                  key={index}
                  onMouseEnter={() => setActiveFeature(index)}
                  className={`border-2 transition-all duration-300 hover:shadow-xl cursor-pointer ${
                    activeFeature === index 
                      ? 'border-blue-500 shadow-lg shadow-blue-100 scale-105' 
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <CardHeader>
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 ${
                      activeFeature === index
                        ? 'bg-gradient-to-br from-blue-600 to-blue-700 shadow-lg shadow-blue-200'
                        : 'bg-blue-50'
                    }`}>
                      <Icon className={`h-7 w-7 ${
                        activeFeature === index ? 'text-white' : 'text-blue-600'
                      }`} />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                    <ul className="mt-4 space-y-2">
                      {feature.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <Check className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-50 to-yellow-50/20">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Mengapa Memilih Hosting RDM dari Kami?
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Kami memahami kebutuhan khusus madrasah dalam mengelola sistem penilaian digital. Dengan pengalaman dan komitmen kami, madrasah Anda dapat fokus pada pendidikan tanpa khawatir masalah teknis.
              </p>
              <div className="space-y-4">
                {mockData.whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 rounded-lg bg-white/60 backdrop-blur-sm border border-blue-100 hover:shadow-md transition-all duration-300">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-yellow-600 flex items-center justify-center flex-shrink-0">
                      {item.icon === 'Shield' ? <Shield className="h-5 w-5 text-white" /> :
                       item.icon === 'Clock' ? <Clock className="h-5 w-5 text-white" /> :
                       <Check className="h-5 w-5 text-white" />}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl bg-gradient-to-br from-blue-100 to-yellow-100 p-8 shadow-2xl">
                <div className="w-full h-full rounded-xl bg-white p-6 flex flex-col justify-center space-y-6">
                  <div className="text-center">
                    <div className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-yellow-600 bg-clip-text text-transparent mb-2">
                      99.9%
                    </div>
                    <div className="text-gray-700 font-medium">Uptime Guarantee</div>
                  </div>
                  <div className="border-t border-gray-200"></div>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">24/7</div>
                      <div className="text-xs text-gray-600">Support</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-yellow-600">Gratis</div>
                      <div className="text-xs text-gray-600">Instalasi</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Dipercaya oleh Madrasah di Indonesia
            </h2>
            <p className="text-lg text-gray-600">
              Bergabunglah dengan puluhan madrasah yang telah merasakan manfaat hosting RDM kami
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {mockData.testimonials.map((testimonial, index) => (
              <Card key={index} className="border-2 border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-500 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic leading-relaxed">"{testimonial.feedback}"</p>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-yellow-600 flex items-center justify-center text-white font-bold text-lg">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.position}</div>
                      <div className="text-xs text-gray-500">{testimonial.school}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 px-6 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto max-w-3xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Pertanyaan yang Sering Diajukan
            </h2>
            <p className="text-lg text-gray-600">
              Temukan jawaban atas pertanyaan umum tentang layanan hosting RDM kami
            </p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {mockData.faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-white border-2 border-gray-200 rounded-lg px-6 hover:border-blue-300 transition-colors"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-600 py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-4 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-blue-600 to-blue-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-yellow-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Siap Beralih ke Hosting RDM Profesional?
          </h2>
          <p className="text-xl text-blue-100 mb-10 leading-relaxed">
            Konsultasikan kebutuhan hosting RDM madrasah Anda dengan tim kami. Dapatkan solusi terbaik dan penawaran khusus hari ini.
          </p>
          <Button 
            onClick={handleWhatsAppClick}
            size="lg" 
            className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-10 py-6 shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 font-semibold"
          >
            <MessageCircle className="mr-2 h-5 w-5" />
            Hubungi Sales Sekarang
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-yellow-400 bg-clip-text text-transparent mb-4">
                nja
              </div>
              <p className="text-gray-400 mb-4 leading-relaxed">
                Penyedia layanan hosting RDM terpercaya untuk madrasah di seluruh Indonesia. Komitmen kami adalah memberikan infrastruktur teknologi terbaik untuk pendidikan.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Layanan</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-blue-400 transition-colors">Hosting RDM</a></li>
                <li><a href="#features" className="hover:text-blue-400 transition-colors">Support Teknis</a></li>
                <li><a href="#features" className="hover:text-blue-400 transition-colors">Instalasi Gratis</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Perusahaan</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#testimonials" className="hover:text-blue-400 transition-colors">Testimoni</a></li>
                <li><a href="#faq" className="hover:text-blue-400 transition-colors">FAQ</a></li>
                <li>
                  <button onClick={handleWhatsAppClick} className="hover:text-blue-400 transition-colors">
                    Hubungi Kami
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-500">
            <p>&copy; {new Date().getFullYear()} nja. Semua hak cipta dilindungi.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;